import { useQuery } from "@tanstack/react-query";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button, Dialog, Spinner } from "./ui.tsx";
import { extOf, fileUrl, isImage, isPdf, isText } from "../lib/files.ts";

export function FilePreview({ path, onClose }: { path: string | null; onClose: () => void }) {
  const name = path?.split("/").pop() ?? "";
  const text = useQuery({
    queryKey: ["file-text", path],
    queryFn: async () => {
      const r = await fetch(fileUrl(path!));
      if (!r.ok) throw new Error(((await r.json().catch(() => ({}))) as { error?: string }).error ?? "Couldn't open the file");
      return r.text();
    },
    enabled: !!path && isText(path),
  });
  return (
    <Dialog open={!!path} onClose={onClose} title={name} wide>
      {path && (
        <div className="space-y-4">
          <div className="max-h-[65vh] overflow-auto rounded-lg border border-[var(--line)] bg-[var(--panel-2)]">
            {isImage(path) ? (
              <img src={fileUrl(path)} alt={name} className="mx-auto max-h-[65vh] object-contain" />
            ) : isPdf(path) ? (
              <iframe src={fileUrl(path)} title={name} className="h-[65vh] w-full bg-white" />
            ) : isText(path) ? (
              text.isPending ? (
                <div className="grid place-items-center py-12"><Spinner /></div>
              ) : text.error ? (
                <p className="p-4 text-sm text-red-700">{text.error.message}</p>
              ) : extOf(path) === "md" ? (
                <div className="prose-krut bg-white p-5"><Markdown remarkPlugins={[remarkGfm]}>{text.data ?? ""}</Markdown></div>
              ) : (
                <pre className="whitespace-pre-wrap break-words p-4 font-mono text-xs leading-relaxed">{text.data}</pre>
              )
            ) : (
              <p className="p-8 text-center text-sm text-[var(--muted)]">No preview for this file type — download it to open.</p>
            )}
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="truncate font-mono text-xs text-[var(--muted)]">workspace/{path}</span>
            <a href={fileUrl(path, true)}><Button variant="primary">Download</Button></a>
          </div>
        </div>
      )}
    </Dialog>
  );
}
